import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AppDispatch, RootState } from '..';
import { setToken } from '../reducers/authReducer';
import { DiaryStatistics, DiaryEntry, RecentDevice } from '../types/diaryTypes';

type ApiResult<T> = {
  data: T;
};

const {
  REACT_APP_BACKEND_URL: BACKEND_URL,
  REACT_APP_BACKEND_FEATURE_BRANCH: BACKEND_FEATURE_BRANCH,
  REACT_APP_BACKEND_API_MGMT_KEY: BACKEND_API_MGMT_KEY,
} = process.env;

const phoenixApi = createApi({
  reducerPath: 'phoenixApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as RootState).auth.token;
      if (!token) {
        const auth = localStorage.getItem('phoenixAuthentication');
        if (auth) {
          const { authenticationToken } = JSON.parse(auth);
          token = authenticationToken;
        }
      }

      if (token) {
        if (BACKEND_FEATURE_BRANCH) {
          headers.set('feature-branch', `${BACKEND_FEATURE_BRANCH}`);
        }

        headers.set('Ocp-Apim-Subscription-Key', `${BACKEND_API_MGMT_KEY}`);
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        throw new Error('No Authentication given');
      }

      return headers;
    },
  }),
  endpoints: builder => ({
    getAuthenticate: builder.query<ApiResult<boolean>, number>({
      query: () => `/phoenixAuthenticate`,
    }),
    getDiariesByUserId: builder.query<
      ApiResult<{ diaryEntries: Array<DiaryEntry>; page: number; pageSize: number; hasNextPage: boolean }>,
      { id: string; page: number; pageSize: number }
    >({
      query: ({ id, page, pageSize }) => `/phoenixUserDiaries?userId=${id}&page=${page}&pageSize=${pageSize}`,
    }),
    getMostUsedSituations: builder.query<
      ApiResult<{ situations: Array<{ id: 'string'; count: number; description: string }> }>,
      { id: string }
    >({
      query: ({ id }) => `/phoenixMostUsedSituations?userId=${id}`,
    }),
    getEntriesState: builder.query<
      ApiResult<{ diaryEntries: number; firstName: string; lastName: string }>,
      { id: string }
    >({
      query: ({ id }) => `/phoenixDiaryEntriesState?userId=${id}`,
    }),
    getRecentDevices: builder.query<ApiResult<{ recentDevices: Array<RecentDevice> }>, { id: string }>({
      query: ({ id }) => `/phoenixRecentDevices?userId=${id}`,
    }),
    getDiaryStatistics: builder.query<ApiResult<DiaryStatistics>, { id: string }>({
      query: ({ id }) => `/phoenixDiaryStatistics?userId=${id}`,
    }),
  }),
});

/**
 * Notifies Phoenix 2 that this site is ready to receive
 * the access token of the employee and to load the diary of a user.
 */
export const notifyPhoenixWindowReady = () => {
  const readyMessage = {
    method: 'POST',
    url: '/ready',
  };
  window.parent.postMessage(readyMessage, '*');
};

/**
 * Notifies Phoenix 2 that the diary of a user was loaded.
 */
export const notifyPhoenixWindowLoaded = () => {
  const readyMessage = {
    method: 'POST',
    url: '/loaded',
  };
  window.parent.postMessage(readyMessage, '*');
};

/**
 * Notifies Phoenix 2 that an error has occured when loading the diary of a user.
 * @param code
 * @param message
 */
export const notifyPhoenixWindowError = (code: string, message: string) => {
  const readyMessage = {
    method: 'POST',
    url: '/error',
    body: {
      code: code,
      message: message,
    },
  };
  window.parent.postMessage(readyMessage, '*');
};

/**
 * Creates a listener that can handle messages sent by the Phoenix 2 window.
 * @param dispatch
 * @returns a callback function that can be attached as an event listener
 */
export const createPhoenixMessageListener = (dispatch: AppDispatch) => {
  return (event: MessageEvent) => {
    if (!event.data) {
      return;
    }

    if (event.data.method === 'POST' && event.data.url === '/load') {
      const { accessToken } = event.data.body;
      dispatch(setToken(accessToken));
    }
  };
};

export const {
  reducerPath,
  reducer,
  middleware,
  useGetAuthenticateQuery,
  useGetDiariesByUserIdQuery,
  useGetMostUsedSituationsQuery,
  useGetEntriesStateQuery,
  useGetRecentDevicesQuery,
  useGetDiaryStatisticsQuery,
} = phoenixApi;
